import type { KeyboardEvent, MouseEvent } from 'react';
import { ExternalLink } from 'lucide-react';
import type { TicketJira } from '@vd/types';
import { Tooltip } from '../primitives/Tooltip';
import { cn } from '../cn';
import styles from './JiraChip.module.css';

/**
 * The address a Jira chip may link to, or null. The server builds it from the
 * connected site, but a card never makes a link of anything but https.
 */
export function jiraIssueHref(url: string | null | undefined): string | null {
  return url && /^https:\/\//i.test(url) ? url : null;
}

// The chip sits inside a card that opens its ticket on a click and on Enter or
// Space (and stops the key's default): the chip's own click and keys stay its own.
const keep = (event: MouseEvent | KeyboardEvent) => event.stopPropagation();

/**
 * Where a ticket stands with Jira, on its card or row (TOADO-708): the issue
 * key, linking to the issue, once sent; "Sending to Jira" on its way; "Jira
 * failed" with the reason in its tooltip. Nothing for a ticket never sent.
 */
export function JiraChip({ jira, className }: { jira?: TicketJira; className?: string }) {
  if (!jira) return null;
  if (jira.state === 'pending') {
    // Short on the card, which is narrow and keeps room for its hover actions;
    // whole for a screen reader and in the tooltip.
    return (
      <Tooltip content="Sending to Jira">
        <span className={cn(styles.chip, styles.pending, className)}>Sending<span className={styles.srOnly}> to Jira</span></span>
      </Tooltip>
    );
  }
  if (jira.state === 'failed') {
    return (
      <Tooltip content={jira.error ? `Not sent to Jira: ${jira.error}` : 'Not sent to Jira'}>
        <span className={cn(styles.chip, styles.failed, className)} tabIndex={0} onClick={keep} onKeyDown={keep}>Jira failed</span>
      </Tooltip>
    );
  }
  if (!jira.issueKey) return null;
  const href = jiraIssueHref(jira.issueUrl);
  if (!href) return <span className={cn(styles.chip, className)}><span className={styles.key}>{jira.issueKey}</span></span>;
  return (
    <Tooltip content={`Open ${jira.issueKey} in Jira`}>
      <a className={cn(styles.chip, styles.link, className)} href={href} target="_blank" rel="noopener noreferrer"
        onClick={keep} onKeyDown={keep} aria-label={`Open ${jira.issueKey} in Jira`}>
        <span className={styles.key}>{jira.issueKey}</span><ExternalLink size={10} aria-hidden="true" className={styles.icon} />
      </a>
    </Tooltip>
  );
}
